/**
 * B2 · Business Catalogue screen — product/service list, add sheet,
 * inline stock adjuster + audit history drawer. Mounted at
 * `/business/:id/catalogue`.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ChevronLeft, Plus, Package, Wrench, Minus, History, Archive, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function BusinessCatalogue() {
  const { t } = useI18n();
  const { id: bid } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [creating, setCreating] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/business/${bid}/catalogue`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("catalogue.load_error", "Could not load catalogue"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  const adjust = async (item, delta) => {
    try {
      await api.post(`/business/${bid}/catalogue/${item.item_id}/inventory`, { delta, reason: delta > 0 ? "Restock" : "Sale" });
      toast.success(t("catalogue.stock_updated", "Stock updated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error", "Something went wrong"));
    }
  };
  const archive = async (item) => {
    if (!window.confirm(t("catalogue.archive.confirm", "Archive this item? Record is preserved."))) return;
    try { await api.post(`/business/${bid}/catalogue/${item.item_id}/archive`); load(); }
    catch { toast.error(t("common.error", "Something went wrong")); }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="business-catalogue">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} aria-label={t("common.back", "Back")}><ChevronLeft size={20}/></button>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">{t("business.section", "Business")}</div>
            <div className="font-black text-stone-900 text-base">{t("catalogue.title", "Catalogue")}</div>
          </div>
          <button onClick={() => setCreating(true)} data-testid="catalogue-add-btn"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black px-3 py-1.5 inline-flex items-center gap-1">
            <Plus size={13}/> {t("catalogue.add", "Add")}
          </button>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 space-y-2">
        {busy && <div className="text-center py-10"><Loader2 className="animate-spin inline text-stone-400"/></div>}
        {!busy && !items.length && (
          <div className="text-center py-16 text-stone-400" data-testid="catalogue-empty">
            <Package size={32} className="inline"/>
            <div className="mt-2 text-sm font-black">{t("catalogue.empty", "No products or services yet")}</div>
          </div>
        )}
        {items.map(it => (
          <div key={it.item_id} data-testid={`catalogue-row-${it.item_id}`}
               className="rounded-2xl bg-white border border-stone-200 p-3 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl grid place-items-center flex-none ${it.type === "product" ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
              {it.type === "product" ? <Package size={18}/> : <Wrench size={18}/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-stone-900 text-sm truncate">{it.name}</div>
              <div className="text-[11px] text-stone-500 truncate">
                {it.category} · {it.price != null ? `${it.currency || "INR"} ${it.price}` : "—"}
                {it.type === "product" && (
                  <> · <AvBadge availability={it.availability}/></>
                )}
              </div>
            </div>
            {it.type === "product" && (
              <div className="flex items-center gap-1">
                <button onClick={() => adjust(it, -1)} aria-label={t("catalogue.decrement", "Decrement stock")}
                        data-testid={`catalogue-stock-dec-${it.item_id}`}
                        className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 grid place-items-center"><Minus size={12}/></button>
                <span className="text-xs font-black w-8 text-center" data-testid={`catalogue-stock-${it.item_id}`}>{it.attrs?.stock ?? "—"}</span>
                <button onClick={() => adjust(it, +1)} aria-label={t("catalogue.increment", "Increment stock")}
                        data-testid={`catalogue-stock-inc-${it.item_id}`}
                        className="w-7 h-7 rounded-full bg-stone-100 hover:bg-stone-200 grid place-items-center"><Plus size={12}/></button>
              </div>
            )}
            <button onClick={() => setHistoryFor(it)} aria-label={t("catalogue.history", "History")}
                    data-testid={`catalogue-history-${it.item_id}`}
                    className="text-stone-400 hover:text-stone-900"><History size={16}/></button>
            <button onClick={() => archive(it)} aria-label={t("catalogue.archive", "Archive")}
                    data-testid={`catalogue-archive-${it.item_id}`}
                    className="text-stone-300 hover:text-red-600"><Archive size={16}/></button>
          </div>
        ))}
      </div>
      {creating && <AddSheet bid={bid} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }}/>}
      {historyFor && <HistoryDrawer bid={bid} item={historyFor} onClose={() => setHistoryFor(null)}/>}
    </div>
  );
}

function AvBadge({ availability }) {
  const cls = {
    in_stock:    "bg-emerald-100 text-emerald-700",
    low_stock:   "bg-amber-100 text-amber-700",
    out_of_stock:"bg-red-100 text-red-700",
    available:   "bg-sky-100 text-sky-700",
    inactive:    "bg-stone-100 text-stone-500",
  }[availability] || "bg-stone-100 text-stone-500";
  return <span className={`inline-block px-1.5 rounded-full text-[9px] font-black ${cls}`}>{availability || "unknown"}</span>;
}

function AddSheet({ bid, onClose, onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ type: "product", name: "", category: "", price: "", stock: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.category.trim()) {
      toast.error(t("catalogue.required", "Name and category required")); return;
    }
    setBusy(true);
    try {
      const attrs = form.type === "product" && form.stock ? { stock: Number(form.stock) } : {};
      await api.post(`/business/${bid}/catalogue`, {
        type: form.type, name: form.name, category: form.category,
        price: form.price ? Number(form.price) : null,
        attrs,
      });
      toast.success(t("catalogue.created", "Created"));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error", "Something went wrong"));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-2" onClick={onClose} data-testid="catalogue-add-sheet">
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="font-black text-stone-900">{t("catalogue.add.title", "Add item")}</div>
        <div className="flex gap-2">
          {["product", "service"].map(k => (
            <button key={k} onClick={() => setForm({...form, type: k})}
                    data-testid={`catalogue-type-${k}`}
                    className={`flex-1 rounded-full text-xs font-black py-2 border ${form.type === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200"}`}>
              {t(`catalogue.type.${k}`, k)}
            </button>
          ))}
        </div>
        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={t("catalogue.name", "Name")} data-testid="catalogue-input-name" className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        <input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder={t("catalogue.category", "Category")} data-testid="catalogue-input-category" className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        <input value={form.price} onChange={e => setForm({...form, price: e.target.value})} type="number" placeholder={t("catalogue.price", "Price")} data-testid="catalogue-input-price" className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        {form.type === "product" && (
          <input value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} type="number" placeholder={t("catalogue.stock", "Initial stock")} data-testid="catalogue-input-stock" className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        )}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2">{t("common.cancel", "Cancel")}</button>
          <button onClick={submit} disabled={busy} data-testid="catalogue-submit"
                  className="flex-1 rounded-full bg-emerald-600 text-white font-black text-xs py-2 disabled:opacity-50">{busy ? t("common.working", "Working…") : t("common.create", "Create")}</button>
        </div>
      </div>
    </div>
  );
}

function HistoryDrawer({ bid, item, onClose }) {
  const { t } = useI18n();
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get(`/business/${bid}/catalogue/${item.item_id}/inventory/history`)
      .then(r => setEvents(r.data.events || []))
      .catch(() => toast.error(t("catalogue.history.error", "Could not load history")));
  }, [bid, item.item_id, t]);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-2" onClick={onClose} data-testid="catalogue-history-drawer">
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="font-black text-stone-900">{t("catalogue.history.title", "Inventory history")} · {item.name}</div>
        {!events.length && <div className="text-center text-stone-400 text-xs py-6">{t("catalogue.history.empty", "No changes yet")}</div>}
        {events.map(e => (
          <div key={e.event_id} className="flex items-center gap-2 py-1 border-b border-stone-100 text-xs">
            <span className={`font-black w-10 text-center ${e.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>{e.delta > 0 ? `+${e.delta}` : e.delta}</span>
            <span className="flex-1 truncate text-stone-700">{e.reason || "—"}</span>
            <span className="text-[10px] text-stone-400">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
        ))}
        <button onClick={onClose} className="w-full rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2 mt-2">{t("common.close", "Close")}</button>
      </div>
    </div>
  );
}
